import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../utils/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { CurrencyInput } from "../components/ui/currency-input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import { Plus, Trash2, Search, ShoppingCart, Printer, X, UserPlus } from "lucide-react";
import { SearchableSelect } from "../components/ui/searchable-select";



const Sales = () => {
  const [sales, setSales] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [nextAccountNumber, setNextAccountNumber] = useState("");
  const [newCustomerData, setNewCustomerData] = useState({
    name: "",
    account_number: "",
    phone: "",
    email: "",
  });
  const [formData, setFormData] = useState({
    customer_id: "",
    date: new Date().toISOString().split("T")[0],
    items: [{ description: "", quantity: "", unit_price: "" }],
    notes: "",
    payment_method: "cash",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [salesRes, customersRes] = await Promise.all([
        axios.get(`${API}/sales`),
        axios.get(`${API}/customers/active/list`),
      ]);
      setSales(salesRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const fetchNextAccountNumber = async () => {
    try {
      const response = await axios.get(`${API}/customers/next-account-number`);
      return response.data.next_account_number;
    } catch (error) {
      console.error("Failed to fetch next account number:", error);
      return "C1001";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (submitting) {
      return;
    }
    
    if (!formData.customer_id) {
      toast.error("Müşteri seçimi zorunludur");
      return;
    }
    if (formData.items.some((item) => !item.description || item.quantity <= 0)) {
      toast.error("Tüm kalem bilgilerini doldurun");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/sales`, formData);
      toast.success("Satış kaydedildi");
      setDialogOpen(false);
      resetForm();
      fetchData();
      
      // 4 second cooldown before allowing next submission
      setTimeout(() => {
        setSubmitting(false);
      }, 4000);
    } catch (error) {
      console.error("Failed to save sale:", error);
      toast.error("İşlem başarısız");
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu satışı silmek istediğinizden emin misiniz?")) return;

    try {
      await axios.delete(`${API}/sales/${id}`);
      toast.success("Satış silindi");
      fetchData();
    } catch (error) {
      console.error("Failed to delete sale:", error);
      toast.error("Silme işlemi başarısız");
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: "", quantity: "", unit_price: "" }],
    });
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index),
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.unit_price) || 0;
      return sum + qty * price;
    }, 0);
  };

  const resetForm = () => {
    setFormData({
      customer_id: "",
      date: new Date().toISOString().split("T")[0],
      items: [{ description: "", quantity: "", unit_price: "" }],
      notes: "",
      payment_method: "cash",
    });
    setShowNewCustomerForm(false);
    setNewCustomerData({ name: "", account_number: "", phone: "", email: "" });
    setNextAccountNumber("");
  };

  const handleShowNewCustomerForm = async () => {
    const nextNum = await fetchNextAccountNumber();
    setNextAccountNumber(nextNum);
    setNewCustomerData({ name: "", account_number: nextNum, phone: "", email: "" });
    setShowNewCustomerForm(true);
  };

  const handleCreateCustomer = async () => {
    if (!newCustomerData.name) {
      toast.error("Müşteri adı zorunludur");
      return;
    }

    try {
      const response = await axios.post(`${API}/customers`, newCustomerData);
      const newCustomer = response.data;
      setCustomers([...customers, newCustomer]);
      setFormData({ ...formData, customer_id: newCustomer.id });
      setShowNewCustomerForm(false);
      setNewCustomerData({ name: "", account_number: "", phone: "", email: "" });
      toast.success(`Müşteri eklendi (${newCustomer.account_number})`);
    } catch (error) {
      console.error("Failed to create customer:", error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Müşteri eklenemedi");
      }
    }
  };

  const handlePrint = (sale) => {
    setSelectedSale(sale);
    setPrintDialogOpen(true);
  };

  const printInvoice = () => {
    window.print();
  };

  const filteredSales = sales.filter(
    (s) =>
      s.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.invoice_no.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const handlePagePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-4 md:space-y-6 print:space-y-4" data-testid="sales-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 md:gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Satışlar
          </h1>
          <p className="text-sm md:text-base text-slate-500 mt-1">Satış kayıtlarını yönetin</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handlePagePrint} className="no-print">
            <Printer className="h-4 w-4 mr-2" /> Yazdır
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" data-testid="add-sale-btn">
                <Plus className="h-5 w-5 mr-2" />
                Yeni Satış
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl mx-4 sm:mx-auto max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Yeni Satış</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Müşteri *</Label>
                    {!showNewCustomerForm && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleShowNewCustomerForm}
                        className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 h-auto py-1 px-2"
                      >
                        <UserPlus className="h-4 w-4 mr-1" />
                        Yeni Müşteri
                      </Button>
                    )}
                  </div>
                  
                  {showNewCustomerForm ? (
                    <div className="space-y-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-emerald-700">Yeni Müşteri Ekle</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowNewCustomerForm(false);
                            setNewCustomerData({ name: "", account_number: "", phone: "", email: "" });
                          }}
                          className="h-6 w-6 p-0 text-slate-400 hover:text-slate-600"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Cari Hesap No"
                          value={newCustomerData.account_number}
                          onChange={(e) => setNewCustomerData({ ...newCustomerData, account_number: e.target.value })}
                          className="font-mono"
                          data-testid="new-customer-account"
                        />
                        <Input
                          placeholder="Müşteri Adı *"
                          value={newCustomerData.name}
                          onChange={(e) => setNewCustomerData({ ...newCustomerData, name: e.target.value })}
                          data-testid="new-customer-name"
                        />
                      </div>
                      <Input
                        placeholder="Telefon"
                        value={newCustomerData.phone}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, phone: e.target.value })}
                        data-testid="new-customer-phone"
                      />
                      <Input
                        placeholder="E-posta"
                        type="email"
                        value={newCustomerData.email}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
                        data-testid="new-customer-email"
                      />
                      <Button
                        type="button"
                        onClick={handleCreateCustomer}
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                        data-testid="create-customer-btn"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Müşteri Ekle
                      </Button>
                    </div>
                  ) : (
                    <SearchableSelect
                      options={customers.map((c) => ({
                        value: c.id,
                        label: c.name,
                        subtitle: c.account_number || c.phone,
                      }))}
                      value={formData.customer_id}
                      onChange={(value) => setFormData({ ...formData, customer_id: value })}
                      placeholder="Müşteri seçin"
                      searchPlaceholder="Müşteri ara..."
                      emptyMessage="Müşteri bulunamadı."
                      data-testid="sale-customer-select"
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Tarih</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    data-testid="sale-date-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Kalemler</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addItem}>
                    <Plus className="h-4 w-4 mr-1" /> Kalem Ekle
                  </Button>
                </div>
                <div className="space-y-2">
                  {formData.items.map((item, index) => (
                    <div key={index} className="p-3 bg-slate-50 rounded-lg space-y-3 sm:space-y-0 sm:flex sm:items-center sm:gap-2">
                      <Input
                        placeholder="Açıklama"
                        value={item.description}
                        onChange={(e) => updateItem(index, "description", e.target.value)}
                        className="flex-1"
                        data-testid={`sale-item-desc-${index}`}
                      />
                      <div className="flex items-center gap-2">
                        <CurrencyInput
                          placeholder="Miktar"
                          value={item.quantity}
                          onChange={(val) => updateItem(index, "quantity", val)}
                          className="w-20 sm:w-24"
                          data-testid={`sale-item-qty-${index}`}
                        />
                        <CurrencyInput
                          placeholder="Birim Fiyat"
                          value={item.unit_price}
                          onChange={(val) => updateItem(index, "unit_price", val)}
                          className="w-28 sm:w-32"
                          data-testid={`sale-item-price-${index}`}
                        />
                        <span className="w-24 sm:w-28 text-right font-medium tabular-nums text-sm">
                          ₺{((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0)).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                        </span>
                        {formData.items.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(index)} className="flex-shrink-0">
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end p-3 bg-slate-100 rounded-lg">
                <div className="text-right">
                  <span className="text-sm text-slate-500">Toplam:</span>
                  <p className="text-xl md:text-2xl font-bold text-slate-900 tabular-nums">
                    ₺{calculateTotal().toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Ödeme Yöntemi</Label>
                  <Select
                    value={formData.payment_method}
                    onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Nakit</SelectItem>
                      <SelectItem value="bank">Banka</SelectItem>
                      <SelectItem value="credit_card">Kredi Kartı</SelectItem>
                      <SelectItem value="deferred">Vadeli</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Notlar</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Ek notlar"
                    rows={1}
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} className="w-full sm:w-auto" disabled={submitting}>
                  İptal
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" data-testid="sale-save-btn" disabled={submitting}>
                  {submitting ? "Kaydediliyor..." : "Kaydet"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3 md:pb-4 p-4 md:p-6">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Satış ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full"
              data-testid="sale-search-input"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredSales.length > 0 ? (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-slate-100">
                {filteredSales.map((sale) => (
                  <div key={sale.id} className="p-4 space-y-3" data-testid={`sale-row-${sale.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900">{sale.customer_name}</p>
                        <p className="text-xs text-slate-500 font-mono">{sale.invoice_no}</p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => handlePrint(sale)}>
                          <Printer className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(sale.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">{sale.date}</span>
                      <span className="font-semibold text-emerald-600 tabular-nums">
                        ₺{sale.total.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Fatura No</TableHead>
                      <TableHead className="font-semibold">Müşteri</TableHead>
                      <TableHead className="font-semibold">Tarih</TableHead>
                      <TableHead className="font-semibold text-right">Tutar</TableHead>
                      <TableHead className="font-semibold text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSales.map((sale) => (
                      <TableRow key={sale.id}>
                        <TableCell className="font-mono text-sm">{sale.invoice_no}</TableCell>
                        <TableCell className="font-medium">{sale.customer_name}</TableCell>
                        <TableCell>{sale.date}</TableCell>
                        <TableCell className="text-right font-semibold tabular-nums">
                          ₺{sale.total.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => handlePrint(sale)}>
                              <Printer className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(sale.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <ShoppingCart className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">{searchTerm ? "Arama sonucu bulunamadı" : "Henüz satış yok"}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Print Dialog */}
      <Dialog open={printDialogOpen} onOpenChange={setPrintDialogOpen}>
        <DialogContent className="max-w-2xl mx-4 sm:mx-auto">
          <DialogHeader>
            <DialogTitle>Fatura Yazdır</DialogTitle>
          </DialogHeader>
          {selectedSale && (
            <div className="print-container p-4 md:p-6 bg-white" id="invoice-print">
              <div className="text-center mb-6 md:mb-8">
                <h2 className="text-xl md:text-2xl font-bold">SATIŞ FATURASI</h2>
                <p className="text-slate-500 text-sm">{selectedSale.invoice_no}</p>
              </div>
              <div className="grid grid-cols-2 gap-4 md:gap-8 mb-4 md:mb-6 text-sm">
                <div>
                  <p className="text-slate-500">Müşteri</p>
                  <p className="font-semibold">{selectedSale.customer_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-slate-500">Tarih</p>
                  <p className="font-semibold">{selectedSale.date}</p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full mb-4 md:mb-6 text-sm">
                  <thead>
                    <tr className="border-b-2 border-slate-200">
                      <th className="text-left py-2">Açıklama</th>
                      <th className="text-right py-2">Miktar</th>
                      <th className="text-right py-2">Birim Fiyat</th>
                      <th className="text-right py-2">Tutar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedSale.items.map((item, index) => (
                      <tr key={index} className="border-b border-slate-100">
                        <td className="py-2">{item.description}</td>
                        <td className="text-right py-2">{item.quantity}</td>
                        <td className="text-right py-2">₺{item.unit_price.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</td>
                        <td className="text-right py-2">₺{(item.quantity * item.unit_price).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-right border-t-2 border-slate-200 pt-4">
                <p className="text-lg md:text-xl font-bold">
                  Toplam: ₺{selectedSale.total.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          )}
          <div className="flex flex-col sm:flex-row justify-end gap-3 no-print">
            <Button variant="outline" onClick={() => setPrintDialogOpen(false)} className="w-full sm:w-auto">Kapat</Button>
            <Button onClick={printInvoice} className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto">
              <Printer className="h-4 w-4 mr-2" /> Yazdır
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sales;
